import { html } from '../lib/html.js';
import { busy, confirm, toast, toastError, toastSuccess } from '../lib/ui.js';
import { accessRequests, DURATIONS, fmtDateTime } from '../services/authorizations.js';
import { categoryOf, vehicleId, vehicleName } from '../services/vehicles.js';
import { Page, go } from './base.js';

/**
 * /pedidos-acesso — o dono (ou a pessoa autorizada) responde aos pedidos que as empresas fizeram pela placa.
 * Cada pedido mostra a empresa, quem pediu e o veículo; o dono escolhe por quantos dias libera e aceita, ou recusa.
 * É para cá que a notificação "Pedido de acesso ao veículo" abre.
 */
class AccessRequestsPage extends Page {
  template() {
    return html`
      <ion-header translucent>
        <ion-toolbar>
          <ion-buttons slot="start"><ion-back-button default-href="/home" text="Voltar"></ion-back-button></ion-buttons>
          <ion-title>Pedidos de acesso</ion-title>
        </ion-toolbar>
      </ion-header>
      <ion-content class="ion-padding" fullscreen>
        <ion-refresher slot="fixed"><ion-refresher-content></ion-refresher-content></ion-refresher>
        <div class="narrow body"></div>
      </ion-content>`;
  }

  setup() {
    this.$('.body').addEventListener('click', (e) => {
      const el = e.target.closest('[data-action]');
      if (el) this.action(el.dataset.action, el);
    });
    this.$('ion-refresher').addEventListener('ionRefresh', async (e) => {
      await this.load();
      e.target.complete();
    });
  }

  onEnter() {
    this.items = null;
    this.days = {}; // prazo escolhido em cada pedido
    this.defaultDays = 7; // o que já vem marcado (definido no portal admin)
    this.load();
  }

  async load() {
    if (!this.items) this.$('.body').innerHTML = '<div class="center"><ion-spinner></ion-spinner></div>';
    try {
      const r = await accessRequests.received();
      this.items = r.items;
      this.defaultDays = r.defaultDurationDays ?? this.defaultDays;
      this.render();
    } catch (err) {
      this.$('.body').innerHTML = html`
        <div class="center">
          <ion-icon name="cloud-offline-outline" class="empty-icon"></ion-icon>
          <p>${err.message}</p>
          <ion-button fill="outline" data-action="reload">Tentar de novo</ion-button>
        </div>`.value;
    }
  }

  render() {
    const items = this.items ?? [];
    if (!items.length) {
      this.$('.body').innerHTML = html`
        <div class="center">
          <ion-icon name="checkmark-circle-outline" class="empty-icon"></ion-icon>
          <h2>Nenhum pedido esperando resposta</h2>
          <p class="hint">Quando uma empresa pedir acesso a um veículo seu pela placa, o pedido aparece aqui.</p>
          <ion-button expand="block" fill="outline" data-action="home">Voltar ao início</ion-button>
        </div>`.value;
      return;
    }
    this.$('.body').innerHTML = html`
      <p class="hint">A empresa autorizada vê o histórico do veículo e registra os serviços. Você pode revogar o acesso quando quiser, na aba Acesso do veículo.</p>
      ${items.map((r) => this.card(r))}`.value;
  }

  card(r) {
    const days = this.days[r.id] ?? this.defaultDays;
    const cat = categoryOf(r.vehicle);
    const id = vehicleId(r.vehicle);
    const car = r.vehicle.nickname || vehicleName(r.vehicle) || id || cat.kind;
    const place = [r.workshop.city, r.workshop.state].filter(Boolean).join('/');
    return html`
      <div class="todo-card" data-request="${r.id}">
        <div class="todo-head">
          <span class="row-icon"><ion-icon name="storefront-outline"></ion-icon></span>
          <div class="row-main">
            <span class="todo-over cat-color-${cat.key}"><ion-icon name="${cat.icon}" aria-hidden="true"></ion-icon>${car}${id && id !== car ? ` · ${id}` : ''}</span>
            <span class="todo-title">${r.workshop.name} pediu acesso</span>
            <span class="todo-sub">${[place, r.requestedBy?.name ? `Pedido por ${r.requestedBy.name}` : ''].filter(Boolean).join(' · ')}</span>
            <span class="todo-sub">Responda até ${fmtDateTime(r.expiresAt)}</span>
          </div>
        </div>
        <div class="segmented four" role="radiogroup" aria-label="Por quanto tempo liberar">
          ${DURATIONS.map((d) => html`<button type="button" role="radio" aria-checked="${days === d}" class="${days === d ? 'on' : ''}" data-action="duration" data-id="${r.id}" data-value="${d}">${d} ${d === 1 ? 'dia' : 'dias'}</button>`)}
        </div>
        <div class="todo-actions">
          <ion-button class="grow" data-action="accept" data-id="${r.id}">Aceitar por ${days} ${days === 1 ? 'dia' : 'dias'}</ion-button>
          <ion-button fill="clear" color="medium" data-action="decline" data-id="${r.id}">Recusar</ion-button>
        </div>
      </div>`;
  }

  async action(name, el) {
    const id = el.dataset.id;
    const r = (this.items ?? []).find((x) => x.id === id);
    switch (name) {
      case 'reload':
        return this.load();
      case 'home':
        return go('/home', 'root');
      case 'duration':
        this.days[id] = Number(el.dataset.value);
        return this.render();
      case 'accept':
        return busy(el, async () => {
          try {
            const days = this.days[id] ?? this.defaultDays;
            const { vehicleId: vid } = await accessRequests.accept(id, days);
            toastSuccess(`${r?.workshop.name ?? 'Empresa'} autorizada por ${days} ${days === 1 ? 'dia' : 'dias'}.`);
            // era o único pedido: abre o veículo na aba Acesso (onde dá para revogar)
            if (this.items.length === 1) return go(`/vehicles/${vid}?aba=acesso`, 'root');
          } catch (err) {
            toastError(err.message);
          }
          await this.load();
        });
      case 'decline': {
        if (!(await confirm({ header: 'Recusar pedido?', message: `${r?.workshop.name ?? 'A empresa'} não recebe acesso ao veículo e é avisada da recusa.`, confirmText: 'Recusar', cancelText: 'Voltar', destructive: true }))) return;
        try {
          await accessRequests.decline(id);
          toast('Pedido recusado.');
        } catch (err) {
          toastError(err.message);
        }
        return this.load();
      }
    }
  }
}

customElements.define('page-access-requests', AccessRequestsPage);
